<script setup>
// 步道詳情：對應設計稿「步道詳情」與「投票成功」兩張圖。
// 兩張稿是同一頁的兩個狀態（詳情本體、投票成功彈窗），因此做成單一頁面 + 彈窗，
// 不是兩支路由。
//
// 資料取自 /front/trail/:id（公開介面，SSR 期間就取回）：步道本身（含整組圖片）、
// 當前活動、票數，以及登入者今天在這條步道所屬分區的那一票還在不在。
// 票數是不分活動的總票數（全站同一套口徑），與首頁精選卡片一致。
import Swiper from 'swiper'
import { A11y, Autoplay, EffectFade, Keyboard, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'
import backIcon from '~/assets/img/trail-back.png'
import shareLineIcon from '~/assets/img/share-line.png'
import shareFbIcon from '~/assets/img/share-facebook.png'
import shareLinkIcon from '~/assets/img/share-link.png'
import voteIcon from '~/assets/img/icon-vote.png'
import voteCardBg from '~/assets/img/trail-vote-card.png'
import voteDoneIcon from '~/assets/img/trail-vote-done.png'
import { buildShareMessage } from '~/utils/shareMessage'
import { VOTE_BUTTON_ENABLED } from '~/utils/voteSwitch'

definePageMeta({ layout: false })

const route = useRoute()
const router = useRouter()
const config = useRuntimeConfig()
const api = useApi()
const { resolveFileUrl } = useFileUrl()
const { isLogged, user, fetchUser } = useAuth()
const { openLogin } = useLoginDialog()
const { ensureProfileComplete, goCompleteProfile, isProfileIncomplete } = useProfileGate()
const { lockScroll } = useScrollLock()

const { data } = await useAsyncData(
  () => `trail-${route.params.id}`,
  () => api.get(`/front/trail/${route.params.id}`).catch(() => null),
  { watch: [() => route.params.id] }
)

// 詳情頁沒有步道就什麼都不用顯示了，直接走 error.vue（搜尋引擎也才拿得到 404）
if (!data.value?.trail) {
  throw createError({
    statusCode: 404,
    statusMessage: '找不到這條步道，可能已經下架了',
    fatal: true
  })
}

const trail = computed(() => data.value.trail)
const quota = computed(() => data.value.zone_quota)

const images = computed(() => (trail.value.images || []).map(resolveFileUrl))
const area = computed(() => [trail.value.city, trail.value.district].filter(Boolean).join(''))

// 分享列貼出去之後，LINE 與 Facebook 都是自己來抓這一頁的 OG 標籤才畫得出卡片，
// 沒有這幾行對方就只看得到一條光禿禿的網址（本機的 localhost 它們連不到，一樣是空的）
const pageOrigin = useRequestURL().origin
const shareUrl = computed(() => pageOrigin + route.fullPath)
const shareImage = computed(() => {
  const src = images.value[0]
  if (!src) return ''
  // og:image 只吃絕對網址，同網域部署時 resolveFileUrl 回的是相對路徑
  return /^https?:\/\//i.test(src) ? src : pageOrigin + src
})
const shareText = computed(() => (trail.value.description || '').slice(0, 200))

useHead({
  title: config.public.siteName,
  meta: computed(() =>
    [
      { name: 'description', content: shareText.value },
      { property: 'og:type', content: 'article' },
      { property: 'og:site_name', content: config.public.siteName },
      { property: 'og:title', content: trail.value.trail_name },
      { property: 'og:description', content: shareText.value },
      { property: 'og:url', content: shareUrl.value },
      shareImage.value ? { property: 'og:image', content: shareImage.value } : null,
      { name: 'twitter:card', content: shareImage.value ? 'summary_large_image' : 'summary' }
    ].filter(Boolean)
  )
})

const votes = computed(() => Number(trail.value.votes || 0))

// 設計稿的資訊列固定三欄，後台沒填的欄位補「—」，版面才不會塌掉
const metrics = computed(() => [
  { label: '級數', value: trail.value.difficulty || '—' },
  {
    label: '里程',
    value: trail.value.distance === null ? '—' : `${Number(trail.value.distance)} km`
  },
  { label: '所需時間', value: trail.value.duration || '—' }
])

/* ---------------- 返回 ---------------- */

// 有上一頁就退回上一頁（多半就是從列表或首頁點進來的，回去還留在原本的分頁與捲動位置），
// 直接開網址進來的沒有上一頁，才導去步道列表
function onBack() {
  if (import.meta.client && window.history.length > 1) return router.back()
  return router.push('/trails')
}

/* ---------------- 圖片輪播 ---------------- */

// 後台可以放多張圖片，詳情頁把整組輪播出來（設計稿封面下方那排指示點）。
// 輪播本體交給 Swiper：自動播、指示點、手勢拖曳、鍵盤左右鍵都由它處理，
// 自己接 pointer 事件那套邊界情況太多（拖到一半切分頁、指標跑出視窗、頭尾接不回去）。
// 用的是核心 API 而不是 swiper/vue 的元件：那組元件在 Vue 3.5 下會噴
// 「Missing ref owner context」警告，自己掛 DOM 反而乾淨，也少一層封裝。
const AUTOPLAY_MS = 4000
const FADE_MS = 700

const coverRef = ref(null)
const pagerRef = ref(null)
let swiper = null

function destroySwiper() {
  if (!swiper) return
  swiper.destroy(true, true)
  swiper = null
}

function initSwiper() {
  destroySwiper()
  if (!coverRef.value) return

  // 只有一張圖時不自動播也不畫指示點，手勢一併關掉（拖得動卻沒東西可換更怪）
  const multi = images.value.length > 1

  swiper = new Swiper(coverRef.value, {
    modules: [A11y, Autoplay, EffectFade, Keyboard, Pagination],
    // 換圖維持設計稿的淡入淡出（位移式輪播在寬螢幕上會晃得太明顯）；
    // fade 在拖曳時會跟著手指的進度交叉淡出，手勢照樣看得到回饋
    effect: 'fade',
    fadeEffect: { crossFade: true },
    speed: FADE_MS,
    loop: multi,
    allowTouchMove: multi,
    // 手勢或點指示點之後仍要繼續自動播，只是重新計時
    autoplay: multi
      ? { delay: AUTOPLAY_MS, disableOnInteraction: false, pauseOnMouseEnter: true }
      : false,
    pagination: multi ? { el: pagerRef.value, clickable: true } : false,
    keyboard: { enabled: true, onlyInViewport: true },
    a11y: {
      prevSlideMessage: '上一張照片',
      nextSlideMessage: '下一張照片',
      paginationBulletMessage: '看第 {{index}} 張照片'
    }
  })

  // 使用者要求減少動態時就不自動播，指示點與手勢仍然可用
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) swiper.autoplay?.stop()
}

onMounted(initSwiper)
onBeforeUnmount(destroySwiper)

// 換步道（同一支路由換 id）時圖片整組換掉，輪播整個重建才不會殘留上一條的狀態。
// flush: 'post' 是為了等 v-for 把新的 slide 畫完再初始化
watch(images, initSwiper, { flush: 'post' })

/* ---------------- 分享 ---------------- */

// 分享列：LINE、Facebook、複製連結
const shares = [
  { key: 'line', label: '分享到 LINE', icon: shareLineIcon },
  { key: 'facebook', label: '分享到 Facebook', icon: shareFbIcon },
  { key: 'link', label: '複製本頁連結', icon: shareLinkIcon }
]

// 開視窗、複製、系統分享面板這些踩過的坑收在 composables/useShare.js
const { share } = useShare()

// 分享出去的是活動邀請，不是步道介紹：登入的人另外附上自己的邀請碼，
// 朋友填了才算得進邀請排行榜（5.11 的邀請碼綁定）。文案與會員中心共用 utils/shareMessage.js
const inviteCode = computed(() => (isLogged.value ? user.value?.invite_code || '' : ''))

// 分享文字不含網址本身，只有複製時要自己把網址接上去
const shareMessage = computed(() => `${buildShareMessage(inviteCode.value)}\n活動網站：`)
const shareCopyText = computed(() => `${shareMessage.value}${shareUrl.value}`)

// 按下分享的那一刻不能再 await（手勢會過期），邀請碼得先備好。
// 重整之後 auth_user 是空的，登入者在這裡先補一次
onMounted(() => {
  if (isLogged.value && !user.value) fetchUser().catch(() => {})
})

/**
 * 回報分享，四區各換一張票
 *
 * **靜默**：不跳提示、失敗也不吭聲。分享成功與否本來就驗證不了（開新視窗之後這一頁就
 * 沒有下文了），跳一個「已加碼」只會讓使用者以為系統知道他真的分享出去了；
 * 而且一天只送一次（後端 uk_share_daily 擋著），連按三顆也只算一次。
 * 拿回來的配額直接覆蓋上去——票數變了，投票鈕的狀態就得跟著變，那不是提示是狀態同步。
 */
async function reportShare(key) {
  // 未登入沒有參與者 token，打了只會拿到 401
  if (!isLogged.value) return
  try {
    const res = await api.post('/front/vote/share', { platform: key })
    const zone = (res?.zones || []).find((z) => z.zone_id === trail.value.zone_id)
    if (zone) {
      data.value = { ...data.value, logged: true, zone_quota: { ...zone, shared: res.shared } }
    }
  } catch {
    // 靜默：分享這件事本來就無法保證，失敗了也不該打斷使用者
  }
}

async function onShare(key) {
  await share(key, {
    url: shareUrl.value,
    text: shareMessage.value,
    copyText: shareCopyText.value,
    notify,
    copyHint: '已複製分享內容,快分享給朋友吧!'
  })
  // 三顆都算分享（含複製連結）：只要按了就送票，不管使用者最後有沒有真的貼出去
  await reportShare(key)
}

// 分享鈕按下去先開加碼彈窗，按下彈窗的按鈕才真的分享。
// 按的是哪一顆先記著，彈窗要把它亮起來，確認後也照這一顆走原本的流程
const shareAsk = ref('')

// 這次按下去加不加得到票，彈窗的文案照這個說——後端的條件是「登入、活動期間、今天還沒領過」
const shareState = computed(() => {
  if (!isLogged.value) return 'guest'
  if (!data.value.activity?.ongoing) return 'off'
  return quota.value?.shared ? 'claimed' : 'ready'
})

/**
 * 按下彈窗的按鈕：照使用者原本按的那一顆分享
 *
 * **中間不能 await**：開新視窗、`navigator.share` 與複製連結都要在使用者手勢裡才不會被
 * 瀏覽器擋下，隔了一個 await 手勢就過期了（`useShare` 的分支也是為此全部同步起手）。
 */
function confirmShare() {
  const key = shareAsk.value
  shareAsk.value = ''
  if (key) onShare(key)
}

/* ---------------- 投票 ---------------- */

const voting = ref(false)

// 這一區今天的票還在不在。未登入時當作還在（按鈕照樣亮著，按下去先帶出登入彈窗）。
// **同一條步道可以重複投**，所以只看票數，不管今天投給了誰——與列表頁同一套判斷
const voted = computed(() => Boolean(quota.value && quota.value.remaining === 0))
const votable = computed(() => data.value.votable && !voted.value)

const buttonText = computed(() => {
  if (!data.value.votable) return '我要投票'
  return voted.value ? '今日票數已用完' : '我要投票'
})

// 按鈕下方那句話：設計稿是「今日中區票數:尚可使用」，依實際狀態換文案
const quotaText = computed(() => {
  const activity = data.value.activity
  if (!data.value.votable) {
    if (!activity) return '目前沒有進行中的活動'
    return activity.upcoming
      ? `活動 ${activity.start_date} 開跑,敬請期待`
      : '本次活動已結束,感謝你的支持'
  }
  if (!quota.value) return '登入後每天各區都能投一票'

  const zone = quota.value.zone_name
  if (quota.value.remaining > 0) return `今日${zone}票數:尚可使用`
  // 票用完了：還沒分享的人指路去分享加碼，分享過的就只剩明天。
  // 票選期間外分享不再加碼（口徑同後端），這時就不能再把人指去分享
  return quota.value.shared || !activity?.ongoing
    ? `今日${zone}票數:已用完,明天再來`
    : `今日${zone}票數:已用完,分享這一頁可再加碼一票`
})

/**
 * 把投票結果套回畫面
 *
 * 不重打一次詳情：投票端點回的就是最新的四區配額與這條步道的最新總票數，
 * 兩者直接覆蓋上去即可（票數與這一頁本來印的是同一個口徑）。
 */
function applyVoteResult(res) {
  const zone = (res?.zones || []).find((z) => z.zone_id === res?.trail?.zone_id)
  data.value = {
    ...data.value,
    logged: true,
    trail: { ...trail.value, votes: Number(res?.trail?.votes ?? trail.value.votes ?? 0) },
    zone_quota: zone ? { ...zone, shared: res.shared } : data.value.zone_quota
  }
}

// 投票前先問「投幾票、確定嗎」（設計稿「投票彈窗」），按下確認才真的送出
const voteConfirm = ref(false)

async function onVote() {
  if (voting.value || !votable.value) return
  // 投票端點只認參與者 token，沒登入就先帶出登入彈窗
  if (!isLogged.value) return openLogin()
  // 個人資料沒填齊就先去會員中心補，補完會回到這一頁
  if (!(await ensureProfileComplete())) return
  voteConfirm.value = true
}

function closeConfirm() {
  if (voting.value) return
  voteConfirm.value = false
}

async function submitVote(votes = 1) {
  if (voting.value) return

  const name = trail.value.trail_name
  voting.value = true
  try {
    const res = await api.post('/front/vote', { trail_id: trail.value.id, votes })
    const zoneName = res?.trail?.zone_name || trail.value.zone_name
    applyVoteResult(res)
    voteConfirm.value = false
    showSuccess(zoneName, name, votes)
  } catch (err) {
    voteConfirm.value = false
    if (isProfileIncomplete(err)) return goCompleteProfile()
    notify(err.message)
  } finally {
    voting.value = false
  }
}

/* ---------------- 彈窗 ---------------- */

// 投票成功（設計稿那張有插圖的）與各種提示共用同一個彈窗，差別只在有沒有插圖與標題
const dialog = ref(null)

function showSuccess(zoneName, trailName, votes = 1) {
  dialog.value = {
    icon: true,
    title: '投票成功',
    text: `已將您在${zoneName}的今日 ${votes} 票投給`,
    highlight: trailName || trail.value.trail_name,
    btn: '確認',
    backToList: true
  }
}

// 投完票就回步道列表去投別區（列表頁投完則留在原地）。上一頁正是列表時用 back，
// 分區與捲動位置才留得住；從首頁或直接開網址進來的，導去這條步道所屬分區的列表
function goListAfterVote() {
  const prev = import.meta.client ? router.options.history.state?.back : ''
  if (typeof prev === 'string' && /^\/trails\/?(\?|$)/.test(prev)) return router.back()
  const zoneKey = data.value?.zone_quota?.zone_key
  return router.push({ path: '/trails', query: zoneKey ? { zone: zoneKey } : {} })
}

function notify(text) {
  dialog.value = { icon: false, title: '', text, highlight: '', btn: '確認' }
}

function closeDialog() {
  const backToList = dialog.value?.backToList
  dialog.value = null
  if (backToList) goListAfterVote()
}

function onKeydown(e) {
  if (e.key === 'Escape') closeDialog()
}

watch(dialog, (open) => {
  if (open) {
    window.addEventListener('keydown', onKeydown)
    lockScroll(true)
    return
  }
  window.removeEventListener('keydown', onKeydown)
  lockScroll(false)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div class="detail">
    <div class="head-fix">
      <SiteHeader active="vote" />
    </div>

    <main class="body">
      <div class="wrap">
        <button class="back" type="button" @click="onBack">
          <img :src="backIcon" width="14" height="14" alt="" aria-hidden="true" />
          返回步道列表
        </button>

        <!-- 輪播交給 Swiper：自動播、指示點、手勢拖曳都由它處理（滑鼠移到圖上會暫停）。
             Swiper 初始化前這裡就是一張被裁切的封面，SSR 出去的畫面不會跳 -->
        <div v-if="images.length" ref="coverRef" class="cover swiper">
          <div class="swiper-wrapper">
            <div v-for="(src, i) in images" :key="src" class="swiper-slide">
              <img
                class="shot"
                :src="src"
                :alt="`${trail.trail_name} 照片 ${i + 1}`"
                draggable="false"
              />
            </div>
          </div>
          <div v-show="images.length > 1" ref="pagerRef" class="swiper-pagination"></div>
          <p v-if="trail.image_source" class="credit">圖片來源：{{ trail.image_source }}</p>
        </div>
        <div v-else class="cover">
          <span class="cover-empty" aria-hidden="true"></span>
        </div>

        <div class="title">
          <div class="title-main">
            <p v-if="area" class="area">{{ area }}</p>
            <h1 class="name">{{ trail.trail_name }}</h1>
          </div>

          <div class="acts">
            <span class="acts-label">分享：</span>
            <button
              v-for="item in shares"
              :key="item.key"
              class="act"
              type="button"
              :title="item.label"
              :aria-label="item.label"
              @click="shareAsk = item.key"
            >
              <img :src="item.icon" width="32" height="32" alt="" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div class="meta">
          <ul class="metrics">
            <li v-for="m in metrics" :key="m.label">
              <b>{{ m.label }}：</b><i>{{ m.value }}</i>
            </li>
          </ul>

          <ul v-if="trail.tags.length" class="tags">
            <li v-for="t in trail.tags" :key="t">{{ t }}</li>
          </ul>
        </div>

        <p v-if="trail.description" class="desc">{{ trail.description }}</p>

        <!-- 投票卡：底圖已把漸層、網點與格紋烘好，拉滿卡片即可 -->
        <section class="vcard" :style="{ backgroundImage: `url(${voteCardBg})` }">
          <div class="vcard-l">
            <span class="vtip">票數即時統計</span>
            <p class="vnum">
              <b><CountUp :value="votes" /></b>
              目前得票數
            </p>
          </div>

          <div v-if="VOTE_BUTTON_ENABLED" class="vcard-r">
            <button class="vbtn" type="button" :disabled="!votable || voting" @click="onVote">
              <img :src="voteIcon" width="22" height="22" alt="" aria-hidden="true" />
              {{ voting ? '投票中…' : buttonText }}
            </button>
            <p class="vquota">{{ quotaText }}</p>
          </div>
        </section>
      </div>
    </main>

    <SiteFooter />

    <!-- 每次投票前先確認張數（設計稿「投票彈窗」），按下確認才真的送出 -->
    <VoteConfirmDialog
      :open="voteConfirm"
      :trail-name="trail.trail_name"
      :cover="images[0] || ''"
      :zone-name="quota?.zone_name || trail.zone_name || ''"
      :remaining="quota?.remaining"
      :submitting="voting"
      @close="closeConfirm()"
      @confirm="submitVote"
    />

    <!-- 分享前先開加碼彈窗，按下按鈕才真的分享並回報加碼 -->
    <ShareBonusDialog
      :open="!!shareAsk"
      :platform="shareAsk"
      :state="shareState"
      @close="shareAsk = ''"
      @confirm="confirmShare()"
    />

    <!-- 投票成功（設計稿「投票成功」）與各種提示共用 -->
    <ClientOnly>
      <Teleport to="body">
        <Transition name="dlg">
          <div v-if="dialog" class="dlg-mask" @click.self="closeDialog()">
            <div class="dlg-panel" role="alertdialog" aria-modal="true" aria-labelledby="dlg-text">
              <img
                v-if="dialog.icon"
                class="dlg-ico"
                :src="voteDoneIcon"
                width="128"
                height="128"
                alt=""
                aria-hidden="true"
              />
              <h2 v-if="dialog.title" class="dlg-title">{{ dialog.title }}</h2>
              <p id="dlg-text" class="dlg-text">
                {{ dialog.text }}<em v-if="dialog.highlight">{{ dialog.highlight }}</em>
              </p>
              <button class="dlg-btn" type="button" @click="closeDialog()">{{ dialog.btn }}</button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style scoped>
.detail {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: var(--site-font);
  color: var(--site-ink);
  background: #fff;
}

/* 頁首釘在畫面最上方，內容捲動時不跟著走（與活動辦法一致） */
.head-fix {
  position: sticky;
  top: 0;
  z-index: 50;
}

.body {
  position: relative;
  flex: 1;
  /* 底部要留給頁尾的山稜線疊上來：稜線是 1440 × 175 等比縮放，
     窄螢幕上只有幾十 px，用固定值會留下一大片空白 */
  padding: 0 0 calc(min(12.15vw, 175px) + 60px);
  background: #fff;
}

.wrap {
  max-width: var(--site-max);
  margin: 0 auto;
  padding: 0 20px;
}

/* ---------------- 返回列表 ---------------- */

.back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 40px 0 20px;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  color: #b0b0b0;
  cursor: pointer;
  transition: color 0.25s;
}

.back img {
  display: block;
  flex: none;
  transition: transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.back:hover {
  color: var(--site-orange);
}

.back:hover img {
  transform: translateX(-3px);
}

/* ---------------- 封面 ---------------- */

.cover {
  position: relative;
  aspect-ratio: 1180 / 496;
  border-radius: 16px;
  overflow: hidden;
  background: #f3f4f5;
  /* 橫向留給輪播的手勢，縱向照常捲頁 */
  touch-action: pan-y;
}

.shot,
.cover-empty {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 拖曳時不要觸發瀏覽器原生的拖圖與選取 */
  user-select: none;
  -webkit-user-drag: none;
}

/* 指示點：Swiper 產生的節點吃不到 scoped 屬性，一律用 :deep() 收 */
.cover :deep(.swiper-pagination) {
  position: absolute;
  left: 50%;
  bottom: 18px;
  display: flex;
  width: auto;
  align-items: center;
  gap: 8px;
  transform: translateX(-50%);
  /* 間距交給 flex gap，Swiper 自己的左右 margin 收掉 */
  --swiper-pagination-bullet-horizontal-gap: 0;
}

/* 目前這張是一顆拉長的白色膠囊，其餘是小圓點（設計稿） */
.cover :deep(.swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  opacity: 1;
  cursor: pointer;
  transition:
    width 0.35s cubic-bezier(0.22, 0.61, 0.36, 1),
    background-color 0.35s;
}

.cover :deep(.swiper-pagination-bullet:hover) {
  background: #fff;
}

.cover :deep(.swiper-pagination-bullet-active) {
  width: 30px;
  background: #fff;
}

/* 圖片來源壓在輪播右下角；整組圖共用一行出處，不跟著換張 */
.credit {
  position: absolute;
  right: 14px;
  bottom: 14px;
  /* 蓋過 Swiper 的 slide；寬度收在右半邊，才不會壓到置中的指示點 */
  z-index: 10;
  max-width: calc(50% - 60px);
  margin: 0;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffffcc;
  font-size: 14px;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* 拖曳手勢要穿過它交給輪播 */
  pointer-events: none;
}

/* 沒設封面時沿用貼近視覺稿照片色調的漸層，版面不開天窗 */
.cover-empty {
  background: linear-gradient(160deg, #dff0d0 0%, #a9cf87 45%, #6f9f5a 100%);
}

/* ---------------- 標題與操作 ---------------- */

.title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin: 20px 0 0;
}

.area {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #8f8f8f;
}

.name {
  margin: 4px 0 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  color: #02050a;
}

.acts {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
}

.acts-label {
  font-size: 14px;
  line-height: 1.6;
  color: #000000;
}

/* 三顆圖示本身就是完整的圓形按鈕，外框交給圖片，這裡只留下點擊區與動態 */
.act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.act img {
  display: block;
  flex: none;
}

.act:hover {
  transform: translateY(-2px) scale(1.06);
}

/* ---------------- 資訊列 ---------------- */

.meta {
  margin: 20px 0 0;
  padding: 14px;
  border: 1px solid #fceedd;
  border-radius: 12px;
  background: linear-gradient(270.41deg, #fffbf4 -36.62%, #ffffff 129.29%);
}

.metrics,
.tags {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;
}

.metrics {
  align-items: center;
  gap: 0 12px;
  font-size: 14px;
  line-height: 1.6;
  font-weight: 500;
}

/* 欄位之間的細分隔線：最後一欄不畫 */
.metrics li {
  position: relative;
  padding-right: 12px;
}

.metrics li::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  width: 1px;
  height: 14px;
  margin-top: -7px;
  background: #e3e3e3;
}

.metrics li:last-child {
  padding-right: 0;
}

.metrics li:last-child::after {
  display: none;
}

.metrics b {
  font-weight: 500;
  color: #02050a;
}

.metrics i {
  font-style: normal;
  color: #808284;
}

.tags {
  gap: 12px;
  margin-top: 12px;
}

.tags li {
  padding: 8px 16px;
  border-radius: 999px;
  background: #ffe696;
  font-size: 16px;
  line-height: 1.6;
  color: #724511;
}

/* ---------------- 描述 ---------------- */

.desc {
  margin: 20px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: #000000;
  /* 後台是純文字欄位，換行要留著才讀得出段落 */
  white-space: pre-line;
}

/* ---------------- 投票卡 ---------------- */

.vcard {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 202px;
  margin: 20px 0 0;
  padding: 40px;
  border-radius: 16px;
  /* 底圖是 1180 × 201 的整張卡，寬高都拉滿；圓角另外由 border-radius 收邊 */
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.vcard-l {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.vtip {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 25px;
  border-radius: 999px;
  background: linear-gradient(90deg, #96f7ba 0%, #63f8fe 100%);
  font-size: 16px;
  line-height: 1.4;
  color: #0d5941;
}

.vnum {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #000000;
}

.vnum b {
  font-size: 50px;
  font-weight: 900;
  letter-spacing: -1px;
  line-height: 1;
}

.vcard-r {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex: none;
}

.vbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 162px;
  height: 52px;
  padding: 0 24px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  box-shadow: 0 6px 16px rgba(var(--site-orange-rgb), 0.28);
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  color: #fff;
  cursor: pointer;
  transition:
    background-color 0.25s,
    box-shadow 0.25s,
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.vbtn img {
  display: block;
  flex: none;
}

.vbtn:hover:not(:disabled) {
  background: var(--site-orange-deep);
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(var(--site-orange-rgb), 0.36);
}

/* 今日已投或活動不在期間：按鈕留在原位但不可按，版面高度才不會跳 */
.vbtn:disabled {
  background: #f0b58c;
  box-shadow: none;
  cursor: not-allowed;
}

.vquota {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  text-align: right;
  color: var(--site-text-sub);
}

/* ---------------- 彈窗 ---------------- */

.dlg-mask {
  position: fixed;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(11, 17, 22, 0.55);
}

.dlg-panel {
  width: 100%;
  max-width: 340px;
  padding: 40px 24px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(var(--site-shadow-rgb), 0.28);
  text-align: center;
}

.dlg-ico {
  display: block;
  width: 128px;
  height: 128px;
  margin: 0 auto 18px;
  object-fit: contain;
  border-radius: 50%;
}

.dlg-title {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
  color: #111111;
}

.dlg-text {
  margin: 0 0 28px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--site-text);
}

.dlg-text em {
  font-style: normal;
  color: var(--site-orange);
}

.dlg-btn {
  display: block;
  width: 100%;
  height: 56px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  transition:
    background-color 0.25s,
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.dlg-btn:hover {
  background: var(--site-orange-deep);
  transform: translateY(-2px);
}

.dlg-enter-active,
.dlg-leave-active {
  transition: opacity 0.25s;
}

.dlg-enter-active .dlg-panel,
.dlg-leave-active .dlg-panel {
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.dlg-enter-from,
.dlg-leave-to {
  opacity: 0;
}

.dlg-enter-from .dlg-panel,
.dlg-leave-to .dlg-panel {
  transform: translateY(16px) scale(0.96);
}

/* ---------------- 響應式 ---------------- */

@media (max-width: 900px) {
  .back {
    margin: 24px 0 16px;
  }

  .cover {
    aspect-ratio: 16 / 10;
    border-radius: 12px;
  }

  .cover :deep(.swiper-pagination) {
    bottom: 12px;
  }

  /* 手機寬度下右下角會壓到指示點，改放右上角並放寬長度 */
  .credit {
    top: 10px;
    right: 10px;
    bottom: auto;
    max-width: calc(100% - 20px);
    font-size: 12px;
  }

  .name {
    font-size: 24px;
  }

  .vcard {
    /* 直式排列後高度不再是 202，底圖跟著拉伸；網點與格紋是裝飾，變形看不太出來 */
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    min-height: 0;
    padding: 24px;
  }

  .vcard-r {
    align-items: stretch;
    gap: 10px;
  }

  .vbtn {
    width: 100%;
  }

  .vquota {
    text-align: center;
  }
}

@media (max-width: 700px) {
  .title {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .acts {
    /* 標題改成直式後分享列自己一行，靠左對齊，也不必再對齊地點 */
    justify-content: flex-start;
    margin-top: 0;
  }

  .meta {
    padding: 14px 16px;
  }

  .metrics {
    gap: 2px 18px;
    font-size: 14px;
  }

  /* 換行後分隔線會落在行尾，看起來像多打了一條，窄螢幕直接用間距分欄 */
  .metrics li {
    padding-right: 0;
  }

  .metrics li::after {
    display: none;
  }

  .desc {
    font-size: 14px;
    line-height: 1.85;
  }

  .vnum b {
    font-size: 38px;
  }

  .dlg-panel {
    padding: 26px 22px 24px;
  }

  .dlg-ico {
    width: 104px;
    height: 104px;
  }
}

@media (max-width: 480px) {
  .cover {
    aspect-ratio: 4 / 3;
  }

  .name {
    font-size: 21px;
  }

  .vtip {
    height: auto;
    padding: 7px 16px;
    font-size: 13px;
    line-height: 1.5;
    /* 窄螢幕一行放不下，讓它自己換行而不是撐破卡片 */
    white-space: normal;
  }

  .vnum {
    font-size: 17px;
  }

  .vnum b {
    font-size: 34px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .back img,
  .act,
  .cover :deep(.swiper-pagination-bullet),
  .vbtn,
  .dlg-btn,
  .dlg-enter-active,
  .dlg-leave-active,
  .dlg-enter-active .dlg-panel,
  .dlg-leave-active .dlg-panel {
    transition: none;
  }
}
</style>
